import { useCallback, useRef, useState } from "react";
import { UploadSimple, FileXls, Spinner } from "@phosphor-icons/react";
import { uploadReport } from "@/lib/procureApi";
import { toast } from "sonner";

export const UploadZone = ({ onUploaded, compact = false }) => {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!/\.(xlsx|xlsm)$/i.test(file.name)) {
        toast.error("Please upload an .xlsx detailed report");
        return;
      }
      setBusy(true);
      try {
        const res = await uploadReport(file);
        const kind = res.kind === "po_query" ? "PO Query" : "Visibility report";
        toast.success(`${kind} loaded · ${res.row_count.toLocaleString()} rows · ${res.reference_date || ""}`);
        onUploaded?.(res);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally {
        setBusy(false);
      }
    },
    [onUploaded]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (compact) {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          data-testid="upload-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          data-testid="upload-button-compact"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="flex items-center gap-2 rounded-sm border border-[#CAD1DB] bg-[#FFFFFF] px-3 py-2 text-sm font-medium text-[#0F172A] transition-colors hover:bg-[#EDEFF3] disabled:opacity-50"
        >
          {busy ? <Spinner size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />}
          {busy ? "Parsing…" : "Upload report"}
        </button>
      </>
    );
  }

  return (
    <div
      data-testid="upload-zone"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-sm border-2 border-dashed px-8 py-20 text-center transition-colors ${
        dragging ? "border-[#F0323E] bg-[#FFFFFF]" : "border-[#CAD1DB] bg-[#F1F3F6] hover:bg-[#FFFFFF]"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xlsm"
        className="hidden"
        data-testid="upload-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-sm border border-[#CAD1DB] bg-[#FFFFFF]">
        {busy ? (
          <Spinner size={30} className="animate-spin text-[#F0323E]" />
        ) : (
          <FileXls size={30} weight="duotone" className="text-[#F0323E]" />
        )}
      </div>
      <p className="text-lg font-semibold text-[#0F172A]">
        {busy ? "Parsing the visibility report…" : "Drop the detailed Visibility Report"}
      </p>
      <p className="mt-2 max-w-md text-sm text-[#64748B]">
        Upload the daily <span className="font-mono text-[#0F172A]">.xlsx</span> detailed report. ProcureFlow rebuilds
        the OR → OQ → OF pipeline, trends and turnaround automatically. Reference&nbsp;2 links each line across stages.
      </p>
      <span className="pf-label mt-6 inline-flex items-center gap-2">
        <UploadSimple size={14} weight="bold" /> Click or drag &amp; drop
      </span>
    </div>
  );
};
